import { Fragment, useContext, useEffect, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useSelector } from 'react-redux';
import { Box, Button, Card, CardContent, CardHeader, Collapse, Container, Divider, Icon, IconButton, List, ListItem, Stack, styled, Tooltip, Typography } from '@mui/material';

import DateTime from 'Lib/components/common/DateTime';
import SideDrawer, { DRAWER_CONTENT_TYPES } from 'Lib/components/layout/components/SideDrawer';
import SidebarContext from 'Lib/contexts/SidebarContext';
import useApi from 'Lib/hooks/useApi';
import useNotify from 'Lib/hooks/useNotify';
import { InitialLoading } from 'Lib/muiComponents/sidebar/InitialLoading';
import FromNow from 'Lib/utilities/fromNow';
import getPagination from 'Lib/utilities/getPagination';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';

import TimelineIcons from './TimelineIcons';

function TimelineButton({ toggleDrawer, ...props }) {
    const { mobile } = screenSize();

    return (
        <>
            <Tooltip title="Timeline" placement="top" arrow>
                <Box sx={{ mx: { xs: 0, sm: 2 } }}>
                    {mobile ? (
                        <IconButton onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.TIMELINE)}>timeline</IconButton>
                    ) : (
                        <IconButton variant="regular" onClick={() => toggleDrawer(DRAWER_CONTENT_TYPES.TIMELINE)}>
                            timeline
                        </IconButton>
                    )}
                </Box>
            </Tooltip>
            <Timeline {...props} />
        </>
    );
}

const TimelineContent = ({ log, isAllExpanded, User }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setIsExpanded(isAllExpanded);
    }, [isAllExpanded]);

    const changesArray = Array.isArray(log.changes?.changes) ? log.changes.changes : log.changes ? [log.changes] : [];
    const filteredArray = changesArray.filter(element => element?.field && !element.field.includes('Date'));

    return (
        <StyledCard>
            <CardHeader
                title={
                    <Box display="flex" justifyContent="start">
                        <TimelineIcons logType={log.logTypeID} />
                        <Typography
                            sx={theme => ({
                                fontWeight: 'bold',
                                fontSize: `${theme.typography.pxToRem(16)} !important`
                            })}
                        >
                            {log.title}
                        </Typography>
                    </Box>
                }
                action={!isEmpty(filteredArray) && <IconButton onClick={() => setIsExpanded(prevState => !prevState)}>{isExpanded ? 'expand_less' : 'expand_more'}</IconButton>}
            />
            <CardContent sx={{ p: '0 0 0 16px' }}>
                <Stack>
                    <StackBox>
                        <StackIcon>calendar_month</StackIcon>
                        <FromNow value={log.dateTimeCreated} />
                    </StackBox>
                    <StackBox>
                        <StackIcon>schedule</StackIcon>
                        <DateTime value={log.dateTimeCreated} localTimeZone />
                    </StackBox>
                    <StackBox>
                        <StackIcon>person</StackIcon>
                        <User id={log.userID} />
                    </StackBox>
                </Stack>
            </CardContent>
            {!isEmpty(filteredArray) && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <CardContent>
                        <Divider sx={{ borderColor: 'common.gray' }} />
                        {filteredArray.map((changes, index) => {
                            return (
                                <List key={index} sx={{ listStyleType: 'disc' }}>
                                    <ListItem disableGutters disablePadding sx={{ display: 'list-item', ml: 3, pt: 2, textAlign: 'left' }}>
                                        {changes.oldValue
                                            ? `${changes.field} has been changed from ${changes.oldValue} to ${
                                                  changes.field.includes('Lease') && !changes.newValue ? 'Vacant' : changes.newValue
                                              }`
                                            : `${changes.field} has been changed to ${changes.newValue} `}
                                    </ListItem>
                                </List>
                            );
                        })}
                    </CardContent>
                </Collapse>
            )}
        </StyledCard>
    );
};

function Timeline() {
    const { isDrawerOpen, drawerContentTypeID } = useSelector(state => state.ui);
    const {
        sidebar,
        apis,
        components: { User }
    } = useContext(SidebarContext);
    const { toastError } = useNotify();

    const { objectID, objectTypeID } = sidebar;

    const [pagination, setPagination] = useState({});
    const [logs, setLogs] = useState([]);

    const [expandAll, setExpandAll] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const getTimeline = useApi(apis.getLogs);

    useEffect(() => {
        if (isDrawerOpen && drawerContentTypeID === DRAWER_CONTENT_TYPES.TIMELINE) {
            setIsLoading(true);
            fetchTimeline();
        } else {
            setLogs([]);
        }
    }, [isDrawerOpen]);

    const fetchTimeline = (page = 1, pageSize = 10, params) => {
        return getTimeline(objectTypeID, objectID, { page, pageSize, ...params })
            .then(response => {
                setLogs(logs.concat(response.data));
                setPagination(getPagination(response));
                setIsLoading(false);
            })
            .catch(error => {
                setIsLoading(false);
                toastError(error);
            });
    };

    const toggleExpand = () => {
        setExpandAll(prevState => !prevState);
    };

    const getNextPage = () => {
        if (pagination.page == pagination.lastPage) return;
        fetchTimeline(parseInt(pagination.page) + 1);
    };

    const scrollRef = useBottomScrollListener(getNextPage, { debounce: 300 });

    const TimelineCard = () => {
        const groupLogsByYear = () => {
            const groupedLogs = {};

            logs.forEach(({ log }) => {
                const year = log.dateTimeCreated.split('-')[0];

                if (!groupedLogs[year]) {
                    groupedLogs[year] = [];
                }

                groupedLogs[year].push(log);
            });

            return groupedLogs;
        };

        const groupedLogs = groupLogsByYear();

        return (
            <Container sx={{ display: 'flex', flexDirection: 'column' }} disableGutters>
                {Object.keys(groupedLogs)
                    .sort((a, b) => {
                        return b - a;
                    })
                    .map(year => (
                        <Fragment key={year}>
                            <GroupedHeader variant="caption">{year}</GroupedHeader>
                            {groupedLogs[year].map((log, index) => (
                                <TimelineContent key={index} log={log} isAllExpanded={expandAll} User={User} />
                            ))}
                        </Fragment>
                    ))}
            </Container>
        );
    };

    return (
        <SideDrawer
            title="Timeline"
            drawerContentTypeID={DRAWER_CONTENT_TYPES.TIMELINE}
            contentRef={scrollRef}
            contentHeader={
                <Box display="flex" justifyContent="end" pt={4}>
                    <Button sx={{ color: 'common.black' }} onClick={toggleExpand} endIcon={<Icon>{expandAll ? 'expand_less' : 'expand_more'}</Icon>}>
                        {expandAll ? 'Collapse All' : 'Expand All'}
                    </Button>
                </Box>
            }
            hideFooter
        >
            {isLoading ? <InitialLoading /> : <TimelineCard />}
        </SideDrawer>
    );
}

const GroupedHeader = styled(Typography)(({ theme }) => ({
    fontSize: `${theme.typography.pxToRem(10)} !important`,
    letterSpacing: '1.5px',
    lineHeight: '18px',
    fontWeight: 'medium',
    paddingLeft: theme.spacing(2)
}));

const StyledCard = styled(Card)(({ theme }) => ({
    background: theme.palette.common.lightblue,
    border: '1px solid rgba(143,143,143,0.3)',
    borderRadius: '12px',
    marginBottom: theme.spacing(4),
    boxShadow: 'none'
}));

const StackBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'start',
    paddingBottom: theme.spacing(2)
}));

const StackIcon = styled(Icon)(({ theme }) => ({
    marginRight: theme.spacing(2)
}));

export { Timeline, TimelineButton, TimelineContent };
